import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";
import SEO from "@/components/SEO";
import { trackBeginCheckout } from "@/lib/analytics";

export default function SubscriptionsPage() {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", start_date: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/subscriptions/plans").then((r) => setPlans(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      trackBeginCheckout({
        kind: "subscription",
        item_id: selected.id,
        item_name: selected.name,
        price: selected.price,
        category: "subscription",
      });
      const { data } = await api.post("/subscriptions/checkout", {
        plan_id: selected.id,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start subscription. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="subscriptions-page">
      <SEO
        title="Seafood Subscription Plans"
        description="Fresh seafood delivered every week, fortnight or month. Curated boxes, chef recipes included, pause or cancel anytime."
        path="/subscriptions"
      />
      <section className="bg-[color:var(--color-primary)] text-white py-20">
        <div className="container-x px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-4">Never Run Out of Fresh</div>
          <h1 className="font-display text-5xl md:text-6xl">Subscription Plans</h1>
          <p className="text-white/70 max-w-2xl mt-4">Fresh fish delivered on your schedule. Curated by our chefs, paused or cancelled anytime.</p>
        </div>
      </section>

      <section className="section bg-[color:var(--color-bg)]">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((p, idx) => (
              <div
                key={p.id}
                className={`bg-white rounded-lg p-8 flex flex-col border ${idx === 1 ? "border-[color:var(--color-accent)] shadow-xl scale-100 md:scale-105" : "border-[color:var(--color-border)]"}`}
                data-testid={`plan-card-${p.id}`}
              >
                {idx === 1 && <div className="text-xs uppercase tracking-widest text-[color:var(--color-accent)] mb-3">Most Popular</div>}
                <h3 className="font-display text-3xl text-[color:var(--color-primary)] mb-1">{p.name}</h3>
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-4">{p.frequency}</div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-display text-5xl text-[color:var(--color-primary)]">£{p.price.toFixed(0)}</span>
                  <span className="text-sm text-gray-500">/ delivery</span>
                </div>
                <p className="text-sm text-[color:var(--color-text-secondary)] mb-6">{p.description}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-[color:var(--color-accent)] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setSelected(p)}
                  data-testid={`subscribe-btn-${p.id}`}
                  className={idx === 1 ? "btn-accent justify-center" : "btn-primary justify-center"}
                >
                  Subscribe Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="subscription-modal">
          <div className="bg-white rounded-lg p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="font-display text-3xl text-[color:var(--color-primary)] mb-1">Subscribe — {selected.name}</h2>
            <p className="text-sm text-gray-500 mb-6">£{selected.price.toFixed(2)} {selected.frequency.toLowerCase()}</p>
            <form onSubmit={submit} className="space-y-4">
              {[
                { name: "name", label: "Full Name", type: "text" },
                { name: "email", label: "Email", type: "email" },
                { name: "phone", label: "Phone", type: "tel" },
                { name: "address", label: "Delivery Address", type: "text" },
                { name: "start_date", label: "Start Date", type: "date" },
              ].map((f) => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest text-gray-600">{f.label}</label>
                  <input
                    required type={f.type}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2"
                    data-testid={`sub-input-${f.name}`}
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setSelected(null)} className="btn-ghost flex-1 justify-center" data-testid="sub-cancel-btn">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-accent flex-1 justify-center" data-testid="sub-submit-btn">
                  {submitting ? "Loading…" : "Continue to Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
